import React from 'react';
import { Box, Grid, Typography, Divider, List, ListItem, ListItemText } from '@mui/material';
import { ISaleOrderDetail } from 'features/salesOrder/types';
import { formatDate } from '../helpers/dateUtils';
import NumberFormat from 'react-number-format';
import logo from '../../../../assets/logo_normal.svg';
import { FlightVatBreakdown } from '../helpers/paraguayVatHelper';

interface OrderHeaderProps {
	salesOrder: ISaleOrderDetail;
	useLocalCurrency?: boolean;
	localCurrency?: string;
	showSummaryDetails?: boolean;
	vatTotals?: FlightVatBreakdown;
}

export const OrderHeader = ({
	salesOrder,
	useLocalCurrency = false,
	localCurrency = 'USD',
	showSummaryDetails = true,
	vatTotals,
}: OrderHeaderProps): JSX.Element => {
	// Filtrar pasajeros invoiceables
	const invoiceablePassengers = salesOrder.passengers.filter((p) => p.invoiceable === true);
	const clientLabel = invoiceablePassengers.length > 1 ? 'Clientes' : 'Cliente';

	return (
		<Box>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
				<Typography variant="h5" component="h2" fontWeight="bold">
					Orden de Venta #{salesOrder.dealId}
				</Typography>
				<Box
					component="img"
					src={logo}
					alt="MeVuelo Logo"
					sx={{
						height: { xs: '40px', sm: '50px' },
						width: 'auto',
						maxWidth: '200px',
					}}
				/>
			</Box>
			<Divider sx={{ my: 2 }} />

			{/* Sección de Cliente(s) */}
			{invoiceablePassengers.length > 0 && (
				<Box sx={{ mb: 3 }}>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
						{clientLabel}
					</Typography>
					{invoiceablePassengers.length === 1 ? (
						<Box>
							<Typography variant="body1" fontWeight="medium">
								{`${invoiceablePassengers[0].name} ${invoiceablePassengers[0].lastName}`.toUpperCase()}
							</Typography>
							{(invoiceablePassengers[0].documentNumber ||
								(invoiceablePassengers[0].documents && invoiceablePassengers[0].documents.length > 0)) && (
								<Typography variant="body2" color="text.secondary">
									Documento:{' '}
									{invoiceablePassengers[0].documentNumber ||
										invoiceablePassengers[0].documents?.[0]?.documentNumber ||
										'-'}
								</Typography>
							)}
						</Box>
					) : (
						<List dense sx={{ py: 0 }}>
							{invoiceablePassengers.map((passenger) => {
								const documentNumber = passenger.documentNumber || passenger.documents?.[0]?.documentNumber || null;
								return (
									<ListItem key={passenger.keyId} sx={{ py: 0.5, px: 0 }}>
										<ListItemText
											primary={`${passenger.name} ${passenger.lastName}`.toUpperCase()}
											secondary={documentNumber ? `Documento: ${documentNumber}` : undefined}
											primaryTypographyProps={{ variant: 'body1', fontWeight: 'medium' }}
											secondaryTypographyProps={{ variant: 'body2' }}
										/>
									</ListItem>
								);
							})}
						</List>
					)}
				</Box>
			)}

			<Grid container spacing={3}>
				{showSummaryDetails && (
					<Grid item xs={12} sm={6} md={3}>
						<Typography variant="body2" color="text.secondary">
							Fecha de Salida
						</Typography>
						<Typography variant="body1" fontWeight="medium">
							{formatDate(salesOrder.travelDepartureDate)}
						</Typography>
					</Grid>
				)}
				{showSummaryDetails && (
					<Grid item xs={12} sm={6} md={3}>
						<Typography variant="body2" color="text.secondary">
							Fecha de Retorno
						</Typography>
						<Typography variant="body1" fontWeight="medium">
							{formatDate(salesOrder.travelReturnDate)}
						</Typography>
					</Grid>
				)}
				{showSummaryDetails && (
					<Grid item xs={12} sm={6} md={3}>
						<Typography variant="body2" color="text.secondary">
							Destinos
						</Typography>
						<Typography variant="body1" fontWeight="medium">
							{salesOrder.destinations || '-'}
						</Typography>
					</Grid>
				)}
				{showSummaryDetails && (
					<Grid item xs={12} sm={6} md={3}>
						<Typography variant="body2" color="text.secondary">
							Monto Total de Venta
						</Typography>
						<Typography variant="body1" fontWeight="bold" color="primary">
							<NumberFormat
								value={
									useLocalCurrency
										? (salesOrder as any).saleAmountInLocalCurrency || salesOrder.saleAmount || 0
										: salesOrder.saleAmount || 0
								}
								displayType="text"
								thousandSeparator="."
								decimalSeparator=","
								decimalScale={2}
								fixedDecimalScale
								prefix={`${useLocalCurrency ? localCurrency : salesOrder.currency} `}
							/>
						</Typography>
						{vatTotals && vatTotals.taxableBase > 0 && (
							<Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
								<Typography variant="caption" color="text.secondary">
									Base imponible:{' '}
									<NumberFormat
										value={vatTotals.taxableBase}
										displayType="text"
										thousandSeparator="."
										decimalSeparator=","
										decimalScale={2}
										fixedDecimalScale
										prefix={`${salesOrder.currency} `}
									/>
								</Typography>
								<Typography variant="caption" color="text.secondary">
									IVA (10%):{' '}
									<NumberFormat
										value={vatTotals.vat}
										displayType="text"
										thousandSeparator="."
										decimalSeparator=","
										decimalScale={2}
										fixedDecimalScale
										prefix={`${salesOrder.currency} `}
									/>
								</Typography>
							</Box>
						)}
					</Grid>
				)}
				{showSummaryDetails && (
					<Grid item xs={12} sm={6} md={3}>
						<Typography variant="body2" color="text.secondary">
							Monto abonado
						</Typography>
						<Typography variant="body1" fontWeight="medium" color="success.main">
							<NumberFormat
								value={
									useLocalCurrency
										? (salesOrder as any).chargedAmountInLocalCurrency || salesOrder.chargedAmount || 0
										: salesOrder.chargedAmount || 0
								}
								displayType="text"
								thousandSeparator="."
								decimalSeparator=","
								decimalScale={2}
								fixedDecimalScale
								prefix={`${useLocalCurrency ? localCurrency : salesOrder.currency} `}
							/>
						</Typography>
					</Grid>
				)}
				{showSummaryDetails && (
					<Grid item xs={12} sm={6} md={3}>
						<Typography variant="body2" color="text.secondary">
							Monto Pendiente
						</Typography>
						<Typography variant="body1" fontWeight="medium" color="warning.main">
							<NumberFormat
								value={
									useLocalCurrency
										? (salesOrder as any).pendingPayAmountInLocalCurrency || salesOrder.pendingPayAmount || 0
										: salesOrder.pendingPayAmount || 0
								}
								displayType="text"
								thousandSeparator="."
								decimalSeparator=","
								decimalScale={2}
								fixedDecimalScale
								prefix={`${useLocalCurrency ? localCurrency : salesOrder.currency} `}
							/>
						</Typography>
					</Grid>
				)}
				{salesOrder.createdBy && (
					<Grid item xs={12} sm={6} md={3}>
						<Typography variant="body2" color="text.secondary">
							Asesor
						</Typography>
						<Typography variant="body1" fontWeight="medium">
							{salesOrder.createdBy}
						</Typography>
					</Grid>
				)}
				{salesOrder.clientReference && (
					<Grid item xs={12} sm={6} md={3}>
						<Typography variant="body2" color="text.secondary">
							Referencia del cliente
						</Typography>
						<Typography variant="body1" fontWeight="medium">
							{salesOrder.clientReference}
						</Typography>
					</Grid>
				)}
			</Grid>
		</Box>
	);
};
